import { useEffect, useMemo, useState } from 'react';
import type { Escala, Pregunta } from '@copathy/shared';
import { esSeccion } from '@copathy/shared';
import {
  ACENTO_BASE,
  ACENTOS,
  PreguntaRespondible,
  type Acento,
} from './PreguntaRespondible';

/**
 * El cuestionario completo **dentro de un teléfono**, paginado por secciones.
 *
 * ── Por qué un marco de teléfono y no una columna angosta ──
 * El trabajador responde desde la app móvil (D-011). Una columna angosta en una
 * página de escritorio se lee como «contenido apretado»; un teléfono dibujado se
 * lee como lo que es: **así se va a ver donde de verdad se responde**. Es el
 * adelanto de la app móvil que todavía no existe — cuando exista, este marco es
 * lo que la valida contra el instrumento real.
 *
 * ── Las páginas ──
 * Cada **sección** abre una página, con su título, subtítulo y color de acento.
 * Página 0 es la portada: nombre, descripción y «Comenzar». Las preguntas que
 * van antes de la primera sección forman una página sin cabecera.
 *
 * La paginación no es decoración: treinta preguntas en una columna son un
 * formulario que no se termina nunca. Partido en páginas con barra de progreso,
 * cada «Siguiente» es una meta corta — es lo que en AppSheet no se podía hacer y
 * la gente abandonaba.
 *
 * ── Qué es interactivo y qué no ──
 * La **navegación es real** —Comenzar, Siguiente, Atrás funcionan— porque probar
 * el recorrido ES probar el instrumento. Las **preguntas son inertes**: acá no se
 * responde nada. M7 les enchufa el estado sin cambiar la forma.
 */

type Pagina = {
  /** La sección que la abre, o null para la portada / preguntas sin sección. */
  seccion: Pregunta | null;
  preguntas: { pregunta: Pregunta; numero: number }[];
};

export function VistaFormulario({
  nombre,
  descripcion,
  preguntas,
  escalas,
  idResaltado,
}: {
  nombre: string;
  descripcion: string | null;
  preguntas: Pregunta[];
  escalas: Escala[];
  /** La pregunta que se está editando: se resalta y se navega hasta su página. */
  idResaltado: number | null;
}) {
  /*
    Repartir en páginas y numerar. La numeración es CONTINUA a través de las
    páginas y salta las secciones: «pregunta 7 de 23» tiene que significar lo
    mismo en la página 1 que en la 4.
  */
  const { paginas, total } = useMemo(() => {
    const lista: Pagina[] = [];
    let actual: Pagina = { seccion: null, preguntas: [] };
    let n = 0;
    for (const p of preguntas) {
      if (esSeccion(p.tipo)) {
        if (actual.seccion !== null || actual.preguntas.length > 0) lista.push(actual);
        actual = { seccion: p, preguntas: [] };
        continue;
      }
      n += 1;
      actual.preguntas.push({ pregunta: p, numero: n });
    }
    if (actual.seccion !== null || actual.preguntas.length > 0) lista.push(actual);
    return { paginas: lista, total: n };
  }, [preguntas]);

  // Página -1 es la portada. Las demás indexan `paginas`.
  const [pagina, setPagina] = useState(-1);

  /*
    Editar una pregunta lleva la previa a SU página. Sin esto, quien edita la
    pregunta 18 mira una portada — y la gracia de la previa en vivo es mirar lo
    que se está tocando.
  */
  useEffect(() => {
    if (idResaltado === null) return;
    const donde = paginas.findIndex((pg) =>
      pg.preguntas.some((x) => x.pregunta.id === idResaltado) ||
      pg.seccion?.id === idResaltado,
    );
    if (donde >= 0) setPagina(donde);
  }, [idResaltado, paginas]);

  // Si el reordenado dejó menos páginas que la que se estaba mirando, retrocede.
  useEffect(() => {
    if (pagina >= paginas.length) setPagina(paginas.length - 1);
  }, [pagina, paginas.length]);

  const actual = pagina >= 0 ? paginas[pagina] : undefined;
  const acento: Acento =
    (actual?.seccion?.color ? ACENTOS[actual.seccion.color] : undefined) ?? ACENTO_BASE;

  const respondidasAntes = paginas
    .slice(0, Math.max(pagina, 0))
    .reduce((suma, pg) => suma + pg.preguntas.length, 0);
  const progreso =
    total === 0 ? 0 : Math.round(((respondidasAntes + (actual?.preguntas.length ?? 0)) / total) * 100);

  return (
    <Telefono>
      {pagina === -1 ? (
        <Portada
          nombre={nombre}
          descripcion={descripcion}
          total={total}
          paginas={paginas.length}
          vacio={preguntas.length === 0}
          alComenzar={() => setPagina(0)}
        />
      ) : (
        <div className="flex h-full flex-col">
          {/* Barra de progreso: la meta corta que hace terminable un formulario largo. */}
          <div className="shrink-0 px-5 pt-4">
            <div className="flex items-center justify-between text-[11px] font-semibold text-marino-400">
              <span>
                {pagina + 1} de {paginas.length}
              </span>
              <span>{progreso}%</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-marino-100">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${progreso}%`, background: acento.fuerte }}
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-3">
            {actual?.seccion && (
              <header
                className="mb-3 rounded-2xl p-4"
                style={{ background: acento.suave }}
              >
                <p
                  className="text-[11px] font-bold uppercase tracking-widest"
                  style={{ color: acento.fuerte }}
                >
                  Sección {paginas.filter((pg, i) => i <= pagina && pg.seccion).length}
                </p>
                <h3 className="mt-1 text-lg font-bold leading-snug text-marino-900">
                  {actual.seccion.emoji && (
                    <span className="mr-1.5" aria-hidden="true">
                      {actual.seccion.emoji}
                    </span>
                  )}
                  {actual.seccion.texto}
                </h3>
                {actual.seccion.ayuda && (
                  <p className="mt-1 text-sm text-marino-500">{actual.seccion.ayuda}</p>
                )}
              </header>
            )}

            {actual && actual.preguntas.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-marino-200 bg-white px-4 py-8 text-center text-sm text-marino-400">
                Esta sección no tiene preguntas todavía.
              </p>
            ) : (
              <div className="space-y-3">
                {actual?.preguntas.map(({ pregunta, numero }) => (
                  <PreguntaRespondible
                    key={pregunta.id}
                    pregunta={pregunta}
                    numero={numero}
                    escala={escalas.find((e) => e.id === pregunta.escalaId)}
                    acento={acento}
                    resaltada={pregunta.id === idResaltado}
                  />
                ))}
              </div>
            )}
          </div>

          <nav className="flex shrink-0 items-center justify-between gap-3 border-t border-marino-100 bg-white px-4 py-3">
            <button
              type="button"
              onClick={() => setPagina(pagina - 1)}
              className="rounded-xl px-3.5 py-2 text-sm font-semibold text-marino-500 transition
                hover:bg-marino-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
            >
              Atrás
            </button>
            {pagina < paginas.length - 1 ? (
              <button
                type="button"
                onClick={() => setPagina(pagina + 1)}
                className="rounded-xl px-5 py-2 text-sm font-bold text-white transition hover:brightness-110
                  focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
                style={{ background: acento.fuerte }}
              >
                Siguiente
              </button>
            ) : (
              <span
                inert
                className="rounded-xl px-5 py-2 text-sm font-bold text-white opacity-90"
                style={{ background: acento.fuerte }}
              >
                Enviar
              </span>
            )}
          </nav>
        </div>
      )}
    </Telefono>
  );
}

function Portada({
  nombre,
  descripcion,
  total,
  paginas,
  vacio,
  alComenzar,
}: {
  nombre: string;
  descripcion: string | null;
  total: number;
  paginas: number;
  vacio: boolean;
  alComenzar: () => void;
}) {
  return (
    <div className="flex h-full flex-col justify-between p-6">
      <div className="pt-8">
        {/* El isotipo es un círculo marino: acá el fondo es claro, va directo. */}
        <img src="/copathy-isotipo.png" alt="" className="h-12 w-12" />
        <h3 className="mt-5 text-2xl font-bold leading-tight text-marino-900">{nombre}</h3>
        {descripcion && <p className="mt-2 text-sm leading-relaxed text-marino-500">{descripcion}</p>}
        {!vacio && (
          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-marino-400">
            {total} {total === 1 ? 'pregunta' : 'preguntas'}
            {paginas > 1 && ` · ${paginas} partes`}
          </p>
        )}
      </div>

      {vacio ? (
        <p className="rounded-2xl border border-dashed border-marino-200 bg-white px-4 py-6 text-center text-sm text-marino-400">
          Lo que agregues en el constructor va apareciendo acá.
        </p>
      ) : (
        <button
          type="button"
          onClick={alComenzar}
          className="w-full rounded-2xl bg-naranja-500 py-3.5 font-bold text-marino-900 transition
            hover:bg-naranja-400 focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
        >
          Comenzar
        </button>
      )}
    </div>
  );
}

/**
 * El marco. Proporción de un teléfono real (~19.5:9), borde grueso y la isla de
 * la cámara. Lo justo para que se lea «teléfono» sin dibujar un iPhone con
 * marca registrada.
 */
function Telefono({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-[min(100%,23.5rem)]">
      <div className="rounded-[2.6rem] border-[10px] border-marino-900 bg-marino-900 shadow-xl">
        <div className="relative h-[44rem] overflow-hidden rounded-[2rem] bg-marino-50/70">
          {/* La isla de la cámara. */}
          <div className="pointer-events-none absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-marino-900" />
          <div className="h-full pt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}
